import React, { useEffect, useRef } from "react";

//design imports from material
import {
  Grid,
  FormLabel,
  TextField,
  Typography,
  Button,
  FormControl,
  Input,
} from "@mui/material";

//layout import
import Layout from "../../layout/Layout";

//form imports
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import StateList, { stateList } from "../../components/StateList";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
//date picker
import dayjs from "dayjs";

import axios from "axios";
import {
  config,
  gstNumberRegExp,
  ifscCodeRegExp,
  panCodeRegExp,
  pincodeRegExp,
  manageCompaniesAPI,
  statusMasterAPI,
  phoneRegExp,
  emailRegExp,
  urlRegExp,
  trnNumber,
} from "../../config";
import { AlertMessage } from "../../components/AlertMessage";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingButton } from "@mui/lab";

import { useNavigate } from "react-router-dom";

import { useState } from "react";

import coLogo from "../../assets/images/coLogo.jpg";

import { emirates } from "../../components/constants";

const ManageCoAdd = () => {
  const navigate = useNavigate();

  const validateUaePhone = (value, isRequired = true) => {
    if (!value) return isRequired ? "Mobile Number is required" : true;

    // Remove spaces/hyphens for clean checking
    const clean = value.replace(/[\s-]/g, "");

    // Accept any country's phone number: optional leading '+', 7-15 digits
    const phoneRegex = /^\+?[0-9]{7,15}$/;
    if (!phoneRegex.test(clean)) {
      return "Enter a valid phone number";
    }

    return true; // ✅ valid
  };

  const validationSchema = Yup.object().shape({
    txtCompanyName: Yup.string()
      .required("Company Name is required")
      .min(3, "Minimum 3 characters")
      .max(100, "Maximum 100 characters")
      .matches(
        /^[a-zA-Z0-9 _-]+$/,
        "Only letters, numbers, spaces, hyphen (-) and underscore (_) are allowed"
      ),

    txtAddress: Yup.string()
      .required("Address is required")
      .min(3, "Street Address must be at least 3 characters")
      .max(100, "Street Address cannot be more than 100 characters")
      .matches(
        /^[a-zA-Z0-9 _,-]+$/,
        "Only letters, numbers, spaces, hyphen (-), comma (,) and underscore (_) are allowed"
      ),

    txtCity: Yup.string()
      .required("Town / City is required")
      .min(3, "Town / City must be at least 3 characters")
      .max(100, "Town / City cannot be more than 100 characters")
      .matches(
        /^[a-zA-Z0-9 _,-]+$/,
        "Only letters, numbers, spaces, hyphen (-), comma (,) and underscore (_) are allowed"
      ),

    txtState: Yup.string()
      .required("Emirate is required")
      .min(3, "Emirate must be at least 3 characters")
      .max(100, "Emirate cannot be more than 100 characters")
      .matches(
        /^[a-zA-Z0-9 _,-]+$/,
        "Only letters, numbers, spaces, hyphen (-), comma (,) and underscore (_) are allowed"
      ),

    intPincode: Yup.string()
      .required("P.O. Box is required")
      .matches(/^[0-9]+$/, "Only numbers are allowed"),

    txtPrimaryPhoneNumber: Yup.string()
      .required("Primary Contact Number is required")
      .test("valid-uae-phone", (value, ctx) => {
        const result = validateUaePhone(value, true);
        return result === true ? true : ctx.createError({ message: result });
      }),

    txtSecondaryPhoneNumber: Yup.string().test(
      "valid-uae-phone",
      (value, ctx) => {
        const result = validateUaePhone(value, true);
        return result === true ? true : ctx.createError({ message: result });
      }
    ),

    txtEmailId: Yup.string()
      .required("Company Email is required")
      .matches(
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        "Enter a valid email address"
      ),

    txtWebsiteLink: Yup.string()
      .required("Website Link is required")
      .matches(urlRegExp, "Please enter a valid website URL"),

    txtTrnNumber: Yup.string()
      .required("TRN is required")
      .matches(trnNumber, "Please enter a valid 15 digit TRN"),

    txtGstNo: Yup.string()
      .required("VAT Number is required")
      .matches(
        /^\d{15}$/,
        "VAT Number must be exactly 15 digits and numeric only"
      ),

    // bank validation
    txtBankName: Yup.string()
      .required("Bank Name is required")
      .min(3, "Minimum 3 characters")
      .max(100, "Maximum 100 characters")
      .matches(
        /^[a-zA-Z0-9 _-]+$/,
        "Only letters, numbers, spaces, hyphen (-) and underscore (_) are allowed"
      ),

    txtBranch: Yup.string()
      .required("BIC is required")
      .matches(
        /^[A-Z]{4}AE[A-Z0-9]{2}([A-Z0-9]{3})?$/,
        "BIC must be 8 or 11 characters: 4-letter bank code, 'AE' country code, 2-character location code, optional 3-character branch code"
      )
      .test(
        "len",
        "BIC must be either 8 or 11 characters long",
        (val) => val && (val.length === 8 || val.length === 11)
      ),
    txtAccountName: Yup.string()
      .required("Account Holder Name is required")
      .min(3, "Minimum 3 characters")
      .max(100, "Maximum 100 characters")
      .matches(
        /^[a-zA-Z0-9 _-]+$/,
        "Only letters, numbers, spaces, hyphen (-) and underscore (_) are allowed"
      ),
    txtIfscCode: Yup.string()
      .required("IBAN Code is required")
      .matches(
        /^AE\d{2}\d{3}\d{16}$/,
        "IBAN must start with 'AE', followed by 2 digits (check digits), 3-digit bank code, and 16-digit account number"
      )
      .length(23, "IBAN must be exactly 23 characters"),
  });

  const [stateName, setStateName] = React.useState("");
  const [companyName, setCompanyName] = React.useState("");

  const formOptions = { resolver: yupResolver(validationSchema) };
  const { register, handleSubmit, reset, formState, setValue } =
    useForm(formOptions);
  const { errors } = formState;

  const [startDate, setStartDate] = React.useState(
    dayjs().format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = React.useState(dayjs().format("YYYY-MM-DD"));

  function onSubmit(data) {
    data.intType = 1;
    data.txtCoLogo = baseImage;
    config.method = "POST";
    config.data = JSON.stringify(data);
    config.url = manageCompaniesAPI;

    axios(config)
      .then(function (response) {
        handleFormReset();
        if (response.data.code == 501) {
          childRef.current.triggerAlert(
            JSON.stringify({
              message: response.data.message,
              messageType: "warning",
            })
          );
        } else {
          childRef.current.triggerAlert(
            JSON.stringify({
              message: response.data.message,
              messageType: "success",
            })
          );

          setTimeout(function () {
            navigate("/manage-companies/list");
          }, 2000);
        }
      })
      .catch(function (error) {
        console.log(error);
      });
  }

  useEffect(() => {
    handleFormReset();
  }, []);

  const handleFormReset = () => {
    let defaultValues = {};
    defaultValues.txtEndDate = endDate;
    reset({ ...defaultValues });
  };

  const childRef = useRef();

  const [baseImage, setBaseImage] = useState(coLogo);

  const uploadImage = async (e) => {
    const file = e.target.files[0];
    const base64 = await convertBase64(file);
    setBaseImage(base64);
  };

  const convertBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);

      fileReader.onload = () => {
        resolve(fileReader.result);
      };

      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  return (
    <Layout>
      <Grid className="employee-form">
        <AlertMessage ref={childRef} />
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              Manage Companies
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Company</Typography>
              <Typography color="text.primary">Manage</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid
            textAlign="right"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <LoadingButton
              size="medium"
              variant="contained"
              color="secondary"
              className="button_first"
              href="/manage-companies/list"
            >
              List Company
            </LoadingButton>
            <LoadingButton
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>

        <form className="form_design" onSubmit={handleSubmit(onSubmit)}>
          {/* Company Name & Address */}
          <h3>Company Name & Address</h3>
          <Grid container spacing={2}>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="name"
                type="text"
                label="Company Name"
                fullWidth
                {...register("txtCompanyName")}
                error={errors.txtCompanyName ? true : false}
                helperText={errors.txtCompanyName?.message}
              />
            </Grid>

            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="address"
                type="text"
                label="Street Address"
                fullWidth
                {...register("txtAddress")}
                error={errors.txtAddress ? true : false}
                helperText={errors.txtAddress?.message}
              />
            </Grid>
          </Grid>

          <div className="empty-space-20"></div>

          <Grid container spacing={2}>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="city"
                type="text"
                label="Town / City"
                fullWidth
                {...register("txtCity")}
                error={errors.txtCity ? true : false}
                helperText={errors.txtCity?.message}
              />
            </Grid>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <FormControl fullWidth>
                <TextField
                  fullWidth
                  select
                  label="Emirate"
                  {...register("txtState", {
                    required: "Emirate is required",
                  })}
                  error={!!errors.txtState}
                  helperText={errors.txtState?.message}
                >
                  {emirates.map((emirate) => (
                    <MenuItem key={emirate.id} value={emirate.name}>
                      {emirate.name}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            </Grid>
          </Grid>

          <div className="empty-space-20"></div>

          <Grid container spacing={2}>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="Pincode"
                type="number"
                label="P.O.Box"
                fullWidth
                {...register("intPincode")}
                error={errors.intPincode ? true : false}
                helperText={errors.intPincode?.message}
              />
            </Grid>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="Primary phone number"
                type="text"
                label="Primary Contact Number"
                fullWidth
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 10,
                }}
                {...register("txtPrimaryPhoneNumber", {
                  required: "Primary phone number is required",
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: "Phone number must be exactly 10 digits",
                  },
                })}
                error={errors.txtPrimaryPhoneNumber ? true : false}
                helperText={errors.txtPrimaryPhoneNumber?.message}
              />
            </Grid>
          </Grid>

          <div className="empty-space-20"></div>

          <Grid container spacing={2}>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="Secondary phone number"
                type="text"
                label="Secondary Contact Number"
                fullWidth
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 10,
                }}
                {...register("txtSecondaryPhoneNumber", {
                  pattern: {
                    value: /^[0-9]{0,10}$/,
                    message: "Phone number must be up to 10 digits",
                  },
                })}
                // {...register("txtSecondaryPhoneNumber")}
                error={errors.txtSecondaryPhoneNumber ? true : false}
                helperText={errors.txtSecondaryPhoneNumber?.message}
              />
            </Grid>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="Company email id"
                type="text"
                label="Company Email Id"
                fullWidth
                {...register("txtEmailId")}
                error={errors.txtEmailId ? true : false}
                helperText={errors.txtEmailId?.message}
              />
            </Grid>
          </Grid>

          <div className="empty-space-20"></div>

          <Grid container spacing={2}>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="Website Link"
                type="text"
                label="Website Link"
                fullWidth
                {...register("txtWebsiteLink")}
                error={errors.txtWebsiteLink ? true : false}
                helperText={errors.txtWebsiteLink?.message}
              />
            </Grid>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="TRN"
                type="text"
                label="TRN"
                fullWidth
                {...register("txtTrnNumber")}
                error={errors.txtTrnNumber ? true : false}
                helperText={errors.txtTrnNumber?.message}
              />
            </Grid>
          </Grid>

          {/* Business details */}
          <h3>Business Details</h3>
          <Grid container spacing={2}>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="gstNo"
                label="VAT No."
                fullWidth
                inputProps={{ inputMode: "numeric", maxLength: 15 }}
                {...register("txtGstNo")}
                error={errors.txtGstNo ? true : false}
                helperText={errors.txtGstNo?.message}
              />
            </Grid>
          </Grid>
          <div className="empty-space-20"></div>

          {/* Bank details */}
          <h3>Bank Details</h3>
          <Grid container spacing={2}>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="bank_name"
                type="text"
                label="Bank Name"
                fullWidth
                {...register("txtBankName")}
                error={errors.txtBankName ? true : false}
                helperText={errors.txtBankName?.message}
              />
            </Grid>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="account_name"
                type="text"
                label="Account Holder"
                fullWidth
                {...register("txtAccountName")}
                error={errors.txtAccountName ? true : false}
                helperText={errors.txtAccountName?.message}
              />
            </Grid>
          </Grid>

          <div className="empty-space-20"></div>

          <Grid container spacing={2}>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="branch"
                type="text"
                label="BIC"
                fullWidth
                {...register("txtBranch")}
                error={errors.txtBranch ? true : false}
                helperText={errors.txtBranch?.message}
              />
            </Grid>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="ifsc_code"
                type="text"
                label="IBAN Code"
                fullWidth
                {...register("txtIfscCode")}
                error={errors.txtIfscCode ? true : false}
                helperText={errors.txtIfscCode?.message}
              />
            </Grid>
          </Grid>

          <div className="empty-space-20"></div>

          {/* Logo Part */}
          <h3>ADD LOGO</h3>
          <Grid item className="avatar-profile" alignItems="left">
            <Grid alignItems="center">
              <Grid className="profile_upload">
                <Button variant="contained" component="label">
                  Upload
                  <input
                    {...register("txtCoLogo")}
                    onChange={(e) => {
                      uploadImage(e);
                    }}
                    hidden
                    accept="image/*"
                    multiple
                    type="file"
                  />
                </Button>
              </Grid>
              <img className="company_avatar_img" src={baseImage} alt="" />
              <h5 color="black">Upload Company Logo</h5>
              <Typography className="note">
                Allowed *.jpeg, *.jpg, *.png
              </Typography>
            </Grid>
          </Grid>

          {/* Button */}
          <Grid container className="submit-button" alignItems="center">
            <Grid
              item
              xs={12}
              lg={12}
              md={12}
              display="flex"
              justifyContent="center"
            >
              <LoadingButton
                size="large"
                variant="contained"
                color="primary"
                type="submit"
                disabled={formState.isSubmitting}
              >
                Submit
              </LoadingButton>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Layout>
  );
};

export default ManageCoAdd;
